
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { CTraderProfile, SafeApiConnection, TradingAccount } from '@/types/trader';
import { useTranslation } from 'react-i18next';

import { TradingAccountsSection } from '@/components/profile/TradingAccountsSection';
import { SimplifiedConnectApiDialog } from '@/components/accounts/SimplifiedConnectApiDialog';
import { useAuth } from '@/contexts/AuthContext';
import { CTraderProfileDetailHeader } from '@/components/ctrader-profile/CTraderProfileDetailHeader';
import { SearchConnectSection } from '@/components/ctrader-profile/SearchConnectSection';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

export default function CTraderProfileDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Check URL for redirect parameters and auto-open dialog
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openConnectDialog') === 'true') {
      console.log('Auto-opening connect dialog based on URL parameter');
      setIsConnectDialogOpen(true);
      
      // Remove the parameter from the URL without reloading the page
      urlParams.delete('openConnectDialog');
      const newUrl = window.location.pathname + 
        (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['ctraderProfile', id],
    queryFn: async (): Promise<CTraderProfile | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('csp_accounts')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: apiConnections, isLoading: isApiConnectionsLoading } = useQuery({
    queryKey: ['apiConnections', id],
    queryFn: async (): Promise<SafeApiConnection[]> => {
      if (!id) return [];
      const { data, error } = await supabase
        .from('api_connections')
        .select('id, csp_account_id, api_name, status, client_id, expire_date, expires_in, last_refresh_attempt, last_balance_sync, balance_sync_status, balance_sync_error, refresh_status, created_at, updated_at')
        .eq('csp_account_id', id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
  });

  const { data: tradingAccounts, isLoading: isTradingAccountsLoading } = useQuery({
    queryKey: ['tradingAccounts', id, apiConnections],
    queryFn: async (): Promise<{ [key: string]: TradingAccount[] }> => {
      if (!apiConnections || apiConnections.length === 0) return {};
      const accountsByConnection: { [key: string]: TradingAccount[] } = {};
      for (const connection of apiConnections) {
        const { data, error } = await supabase
          .from('trading_accounts')
          .select('*')
          .eq('api_connection_id', connection.id);
        if (error) throw error;
        accountsByConnection[connection.id] = data || [];
      }
      return accountsByConnection;
    },
    enabled: !!apiConnections && apiConnections.length > 0,
  });

  const handleToggleConnection = async (apiConnectionId: string, shouldConnect: boolean) => {
    try {
      if (shouldConnect) {
        const { error } = await supabase.rpc('reconnect_ctrader_profile', {
          csp_account_id: id,
          api_connection_ids: [apiConnectionId]
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.rpc('disconnect_ctrader_profile', {
          csp_account_id: id,
          api_connection_ids: [apiConnectionId]
        });
        if (error) throw error;
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['apiConnections', id] }),
        queryClient.invalidateQueries({ queryKey: ['tradingAccounts', id] }),
        queryClient.invalidateQueries({ queryKey: ['ctraderProfile', id] })
      ]);

      toast({
        title: shouldConnect ? t('account_reconnected') : t('account_disconnected'),
        description: t('account_connection_status_changed', {
          status: shouldConnect ? t('reconnected') : t('disconnected')
        })
      });
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failed_toggle_connection', {
          action: shouldConnect ? t('reconnect') : t('disconnect')
        }),
        variant: "destructive"
      });
    }
  };

  const handleDeleteApiConnection = async (apiConnectionId: string) => {
    try {
      const { error } = await supabase
        .from('api_connections')
        .delete()
        .eq('id', apiConnectionId);
      
      if (error) throw error;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['apiConnections', id] }),
        queryClient.invalidateQueries({ queryKey: ['tradingAccounts', id] }),
      ]);

      toast({
        title: t('account_deleted'),
        description: t('account_removed_desc')
      });
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failed_to_delete_account'),
        variant: "destructive"
      });
    }
  };

  const getFilteredTradingAccounts = () => {
    if (!tradingAccounts || !apiConnections) return [];
    
    const allAccounts: Array<{ tradingAccount: TradingAccount; apiConnection: SafeApiConnection }> = [];
    apiConnections.forEach(connection => {
      const accounts = tradingAccounts[connection.id] || [];
      accounts.forEach(account => {
        allAccounts.push({ tradingAccount: account, apiConnection: connection });
      });
    });
    
    if (!searchQuery) return allAccounts;
    
    const query = searchQuery.toLowerCase();
    return allAccounts.filter(({ tradingAccount, apiConnection }) => {
      return (
        apiConnection.api_name.toLowerCase().includes(query) ||
        apiConnection.client_id.toLowerCase().includes(query) ||
        tradingAccount.number_account_trading.toLowerCase().includes(query)
      );
    });
  };

  const filteredAccounts = getFilteredTradingAccounts();
  const connectedCount = filteredAccounts.filter(({ apiConnection }) => apiConnection.status === 'connected').length;
  const disconnectedCount = filteredAccounts.filter(({ apiConnection }) => apiConnection.status === 'disconnected').length;

  const isLoading = isProfileLoading || isApiConnectionsLoading || isTradingAccountsLoading;

  return (
    <div className={`space-y-${isMobile ? '4' : '6'} ${isMobile ? 'max-w-full mx-auto px-3' : 'max-w-7xl mx-auto'}`}>
      <CTraderProfileDetailHeader 
        profile={profile} 
        isLoading={isProfileLoading}
        connectedCount={connectedCount}
        disconnectedCount={disconnectedCount}
        totalAccounts={filteredAccounts.length}
      />

      <SearchConnectSection 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onOpenConnectDialog={() => {
          console.log('Manual opening of simplified connect dialog');
          setIsConnectDialogOpen(true);
        }}
      />

      <div data-tour="accounts-section">
        <TradingAccountsSection
          isLoading={isLoading}
          filteredAccounts={filteredAccounts}
          searchQuery={searchQuery}
          onToggleConnection={handleToggleConnection}
          onDeleteApiConnection={handleDeleteApiConnection}
          profile={profile}
          onOpenConnectDialog={() => setIsConnectDialogOpen(true)}
        />
      </div>

      <SimplifiedConnectApiDialog 
        open={isConnectDialogOpen}
        onOpenChange={setIsConnectDialogOpen}
        cspAccountId={id || ''}
        onSubmit={async () => {
          setIsConnectDialogOpen(false);
          // Invalidate queries to refresh the data
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['apiConnections', id] }),
            queryClient.invalidateQueries({ queryKey: ['tradingAccounts', id] })
          ]);
        }}
      />
    </div>
  );
}
